import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import {
  attachClosestEdge,
  type Edge,
  extractClosestEdge,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { DropIndicator } from '@atlaskit/pragmatic-drag-and-drop-react-drop-indicator/box';
import { Box, xcss } from '@atlaskit/primitives';
import { useTab } from '@atlaskit/tabs';
import { token } from '@atlaskit/tokens';
import { type DatabaseView } from '@databases/types/database/view';
import { type ViewPanelSettingType } from '@databases/views/types';
import React, {
  createContext,
  type FunctionComponent,
  memo,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { type ReorderingProps } from './ViewActions';
import { ViewTabContent } from './ViewTabContent';

const CustomTabStyles = xcss({
  // this is needed to make sure the drop indicator doesn't get cut off
  overflow: 'visible !important',
});

export const InstanceIdContext = createContext<symbol | null>(null);

type DraggingProps = {
  tabIndex: number;
  closestEdge: Edge | null;
  setClosestEdge: (edge: Edge | null) => void;
  draggedOverIndex: number | null;
  setDraggedOverIndex: (index: number | null) => void;
};

type DraggableViewTabProps = {
  contentId: string;
  view: DatabaseView;
  isActive: boolean;
  isDefault: boolean;
  isReadonly: boolean;
  showViewSettings?: boolean;
  isUnsavedView?: boolean;
  openViewSettingsPanel: (activeViewSetting: ViewPanelSettingType | null) => void;
  draggingProps: DraggingProps;
  reorderingProps: ReorderingProps;
};

type ItemData = {
  type: 'tab';
  view: DatabaseView;
  index: number;
  instanceId: symbol | null;
};

const getItemData = (
  view: DatabaseView,
  index: number,
  instanceId: symbol | null
): ItemData => {
  return {
    type: 'tab',
    view,
    index,
    instanceId,
  };
};

export const DraggableViewTab: FunctionComponent<DraggableViewTabProps> = memo(
  function DraggableViewTab({
    view,
    contentId,
    isActive,
    isDefault,
    isReadonly,
    openViewSettingsPanel,
    showViewSettings = true,
    isUnsavedView,
    draggingProps,
    reorderingProps,
  }) {
    const {
      tabIndex,
      closestEdge,
      setClosestEdge,
      draggedOverIndex,
      setDraggedOverIndex,
    } = draggingProps;
    const { handleReorder, isRightmostTab, rightmostTabIndex } = reorderingProps;
    const ref = useRef<HTMLDivElement | null>(null);
    const instanceId = useContext(InstanceIdContext);
    const [isDragging, setIsDragging] = useState(false);

    useEffect(() => {
      if (isUnsavedView || isReadonly) {
        return;
      }
      const el = ref.current!;
      const data = getItemData(view, tabIndex, instanceId);
      return combine(
        draggable({
          element: el,
          getInitialData: () => data,
          onDragStart: () => setIsDragging(true),
          onDrop: () => setIsDragging(false),
        }),
        dropTargetForElements({
          element: el,
          getData({ input }) {
            return attachClosestEdge(data, {
              element: el,
              input,
              allowedEdges: ['left', 'right'],
            });
          },
          getIsSticky: () => true,
          canDrop: ({ source }) =>
            source.data.instanceId === instanceId &&
            source.data.type === 'tab' &&
            source.data.content !== view,
          onDragEnter: () => {},
          onDragLeave: () => setClosestEdge(null),
          onDrop: () => setClosestEdge(null),
          onDrag({ self, source }) {
            const isSource = source.element === el;
            if (isSource) {
              setClosestEdge(null);
              return;
            }

            const closestEdge = extractClosestEdge(self.data);
            setDraggedOverIndex(tabIndex);

            const sourceIndex = source.data.index;
            if (typeof sourceIndex !== 'number') {
              return;
            }

            const isItemBeforeSource = tabIndex === sourceIndex - 1;
            const isItemAfterSource = tabIndex === sourceIndex + 1;

            const isDropIndicatorHidden =
              (isItemBeforeSource && closestEdge === 'right') ||
              (isItemAfterSource && closestEdge === 'left');

            if (isDropIndicatorHidden) {
              setClosestEdge(null);
              return;
            }

            setClosestEdge(closestEdge);
          },
        })
      );
    }, [
      tabIndex,
      instanceId,
      setClosestEdge,
      setDraggedOverIndex,
      isUnsavedView,
      view,
      isReadonly,
    ]);

    const tabAttributes = useTab();

    return (
      <Box ref={ref} xcss={CustomTabStyles} {...tabAttributes}>
        {/* Needed as a node for drop indicator to render on, otherwise clips */}
        <Box
          xcss={xcss({
            paddingBottom: 'space.025',
            paddingTop: 'space.025',
            opacity: isDragging ? '0.4' : '1',
            position: 'relative',
            ':hover': {
              backgroundColor: 'color.background.neutral',
            },
          })}
        >
          <ViewTabContent
            contentId={contentId}
            view={view}
            isActive={isActive}
            isDefault={isDefault}
            isReadonly={isReadonly}
            openViewSettingsPanel={openViewSettingsPanel}
            showViewSettings={showViewSettings}
            isUnsavedView={isUnsavedView}
            isDragging={isDragging}
            reorderingProps={{
              handleReorder,
              isRightmostTab,
              rightmostTabIndex,
            }}
          />
          {!isUnsavedView && draggedOverIndex === tabIndex && closestEdge && (
            <Box testId="drop-indicator">
              <DropIndicator
                edge={closestEdge}
                gap={
                  // For first and last elem in tab list, lessen gap to prevent
                  // overflow
                  isRightmostTab && closestEdge === 'right'
                    ? token('space.100', '8px')
                    : tabIndex === 0 && closestEdge === 'left'
                    ? token('space.negative.100', '-8px')
                    : token('space.200', '16px')
                }
              />
            </Box>
          )}
        </Box>
      </Box>
    );
  }
);
